import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';

// ───────────────────────────────────────────────────────────────────────────
// Checkout Funnel — a conversion-funnel walk-through of checkout telemetry.
//
// Reuses the SAME underlying data as the Telemetry page (prod telemetry mirrored
// into Neon telemetry_events), but answers the five questions Greg walks through:
//   1. How many checkout sessions started?
//   2. Of those, how many placed an order?
//   3. Of the non-orderers, at what step did they stop?
//   4. When they stopped, why? (error / walked away / closed)
//   5. Which stopped sessions are REAL issues (tiered) — not "sat 15 min then ordered"?
//
// Prod is served by the SQL action=funnel aggregation (one row per session).
// SB-1 has no Neon mirror, so we pull raw events and derive the same signal rows
// client-side via signalsFromEvents(). Both feed classifyFunnel().
// ───────────────────────────────────────────────────────────────────────────

const FN = '/.netlify/functions/checkout-telemetry';

const CHART = { navy: '#00395C', orange: '#DC582A', peach: '#FCD299', green: '#3fb950' };

// Furthest-stage rank map — mirrors reference_checkout_funnel_breakdown_query.md
// and the SQL CASE in checkout-telemetry.js (keep the three in sync).
const STAGES = [
  { rank: 10, label: 'Order placed' },
  { rank: 9,  label: 'Submitting to BC (no success)' },
  { rank: 8,  label: 'Passed pre-submit gate' },
  { rank: 7,  label: 'Place-Order handler ran' },
  { rank: 6,  label: 'Clicked Place Order' },
  { rank: 5,  label: 'Reached Payment step' },
  { rank: 4,  label: 'Submitted Billing (sparse marker)' },
  { rank: 3,  label: 'Committed Shipping' },
  { rank: 2,  label: 'Reached Shipping step' },
  { rank: 1,  label: 'Loaded checkout, never advanced' },
];
const stageLabel = (rank) => (STAGES.find(s => s.rank === rank) || {}).label || ('Rank ' + rank);

const RANK_MAP = {
  'order.submit.success': 10,
  'Payment.handleSubmit.submitOrder.before': 9,
  'order.preSubmit.snapshot': 8,
  'Payment.handleSubmit.entered': 7,
  'payment.placeOrder.click': 6,
  'paymentForm.mounted': 5,
  'billing.continue.call': 4,
  'setShippingRate.ok': 3,
  'handleSingleShippingSubmit.call': 3,
  'Checkout.handleShippingNextStep': 3,
  'moveToShipping.entered': 2,
  'checkout.mount': 1,
  'checkout.loadCheckout.resolved': 1,
};

// ── "Why they stopped" — honest decomposition. page-lifecycle events cannot
// separate closed-tab vs closed-browser vs backgrounded-tab; all surface as
// page.visibility=hidden. So we bucket, not pretend precision.
const WHY = {
  error:      { label: 'Error',          color: '#f85149', blurb: 'Submit or unhandled error fired.' },
  walkedAway: { label: 'Walked away',    color: '#d29922', blurb: 'Tab hidden (switched tab, closed tab, or closed browser — indistinguishable).' },
  lostFocus:  { label: 'Lost focus only',color: '#58a6ff', blurb: 'Window blurred but tab never hidden (switched app/window).' },
  none:       { label: 'No signal',      color: '#8b949e', blurb: 'No error and no tab-away signal — just went quiet.' },
};
function whyStopped(s) {
  if (s.submitError || s.unhandledError) return 'error';
  if (s.hidden) return 'walkedAway';
  if (s.blurred) return 'lostFocus';
  return 'none';
}

// Checkout-step model for ABANDONMENT (sessions that didn't order and aren't a
// real/critical issue). Each abandoned session lands in exactly one step = the
// furthest step it reached. "Logged in" splits the Customer step from the VIP
// step (you can't be stuck on VIP without signing in first).
const STEPS = [
  { key: 'customer',   label: 'Step 1 · Customer / Login' },
  { key: 'vip',        label: 'Step 2 · VIP' },
  { key: 'shipping',   label: 'Step 3 · Shipping' },
  { key: 'payment',    label: 'Step 4 · Payment' },
  { key: 'placeOrder', label: 'Step 5 · Place Order' },
];
function abandonStep(s) {
  if (s.clickedPlaceOrder) return 'placeOrder'; // clicked Place Order, never completed (no error)
  if (s.maxRank >= 5) return 'payment';         // reached the Payment step
  if (s.maxRank >= 2) return 'shipping';        // reached Shipping (past the VIP step)
  if (s.loggedIn) return 'vip';                 // signed in but never got past VIP
  return 'customer';                            // never advanced past Customer / Login
}

// ── Reclassification rules (admin overrides) ────────────────────────────────
// Reclassification is MANUAL and per-session only — every flagged error stays in
// Real issues until an admin explicitly handles it (so nothing gets auto-masked).
// A per-session decision moves ONE session out of Real issues (or 'keep' pins it
// in). Matching on PII-free signatures is informational only (prefill/labels).
function parseSig(sig) {
  const i = sig.indexOf('|'); const j = i < 0 ? -1 : sig.indexOf('|', i + 1);
  if (i < 0 || j < 0) return { event: sig, kind: '', status: '' };
  return { event: sig.slice(0, i), kind: sig.slice(i + 1, j), status: sig.slice(j + 1) };
}
// Human-readable slug shown on a reclassified / per-session-decision row.
function ruleSlug(rule) {
  const sid = String(rule.sessionId || '').slice(0, 18);
  return 'session ' + sid + '… → ' + (rule.category === 'keep' ? 'kept in errors' : rule.category);
}
// The dominant error signature of a session, shown when reclassifying.
function topSig(s) {
  const sigs = (s.errSigs || []).map(parseSig);
  // Prefer a sig that carries an errorName (more specific), else first.
  return sigs.find(p => p.kind) || sigs[0] || null;
}

// Per-session triage vocabulary (review flag + outcome tag).
const REVIEW_STATUS = {
  open:          { label: 'Open',          color: '#b42318', bg: '#fdeceb' },
  investigating: { label: 'Investigating', color: '#b45309', bg: '#fef6e7' },
  resolved:      { label: 'Resolved',      color: '#1a7f37', bg: '#e9f6ec' },
  wontfix:       { label: "Won't fix",     color: '#57606a', bg: '#f3f4f6' },
};
const REVIEW_ORDER = ['open', 'investigating', 'resolved', 'wontfix'];
const OUTCOME_TAG = { bug: 'Real bug', external: 'External', expected: 'Expected', fixed: 'Fixed', duplicate: 'Duplicate' };
function StatusBadge({ status }) {
  const st = REVIEW_STATUS[status] || REVIEW_STATUS.open;
  return <span style={{ fontSize: 9.5, fontWeight: 700, color: st.color, background: st.bg, borderRadius: 4, padding: '2px 6px', textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>{st.label}</span>;
}
function TagChip({ tag }) {
  if (!tag || !OUTCOME_TAG[tag]) return null;
  return <span style={{ fontSize: 9.5, fontWeight: 600, color: '#374151', background: '#eef0f2', borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap' }}>{OUTCOME_TAG[tag]}</span>;
}

// A manually-created SIGNATURE rule auto-applies to any stopped session whose
// error signatures match it (event required; errorName/status optional). These
// are admin-curated only -- the funnel never invents or learns rules.
function matchSignatureRule(session, signatureRules) {
  if (!signatureRules || !signatureRules.length) return null;
  const sigs = (session.errSigs || []).map(parseSig);
  // Most-specific match wins regardless of creation order: a rule pinning both
  // errorName + status beats one pinning only one, beats an event-only rule.
  let best = null, bestScore = -1;
  for (const r of signatureRules) {
    for (const sg of sigs) {
      if (r.event !== sg.event) continue;
      if (r.errorName && r.errorName !== sg.kind) continue;
      if (r.status && String(r.status) !== String(sg.status)) continue;
      const score = (r.errorName ? 1 : 0) + (r.status ? 1 : 0);
      if (score > bestScore) { best = r; bestScore = score; }
      break;
    }
  }
  return best;
}

function classifyFunnel(sessions, rules) {
  const reached = sessions.length;
  const completedSessions = sessions.filter(s => s.completed);
  const stopped = sessions.filter(s => !s.completed);

  // Per-session triage decisions (manual override) win over telemetry typing.
  const sessionDecisions = new Map();
  for (const r of (rules || [])) {
    if (r.scope === 'session' && r.sessionId) sessionDecisions.set(r.sessionId, r);
  }
  const signatureRules = (rules || []).filter(r => r.scope === 'signature' && r.event);
  const reviewOf = (d) => d ? { status: d.reviewStatus || 'open', tag: d.outcomeTag || null, note: d.note || '', ruleId: d.id } : null;
  const reclassOf = (d) => ({ category: d.category, note: d.note, ruleId: d.id, slug: ruleSlug(d) });

  // Type each stopped (non-completed) session strictly by TYPE, telemetry-derived
  // from the emit-side classification (live on prod 2026-06-22). Priority:
  // genuine error (level=error) > external > friction > stuck-submit (no category)
  // > abandonment. A manual decision overrides: friction/external move it,
  // 'keep' pins it to Errors. errSigs/category come from the funnel signal rows.
  const errors = [], friction = [], external = [], abandonedSessions = [], reclassified = [];
  for (const s of stopped) {
    const decision = sessionDecisions.get(s.sessionId);
    if (decision) {
      const rv = reviewOf(decision);
      if (decision.category === 'friction') { const row = { ...s, _review: rv, _reclass: reclassOf(decision) }; friction.push(row); reclassified.push(row); continue; }
      if (decision.category === 'external') { const row = { ...s, _review: rv, _reclass: reclassOf(decision) }; external.push(row); reclassified.push(row); continue; }
      errors.push({ ...s, _review: rv }); continue;   // 'keep'
    }
    const sigRule = matchSignatureRule(s, signatureRules);
    if (sigRule && (sigRule.category === 'friction' || sigRule.category === 'external')) {
      const slug = [sigRule.event, sigRule.errorName, sigRule.status].filter(Boolean).join(' / ') + ' -> ' + sigRule.category;
      const row = { ...s, _reclass: { category: sigRule.category, note: sigRule.note, ruleId: sigRule.id, slug } };
      (sigRule.category === 'friction' ? friction : external).push(row);
      reclassified.push(row);
      continue;
    }
    if (s.hasErrorLevel) { errors.push(s); continue; }
    if (s.hasExternal) { external.push(s); continue; }
    if (s.hasFriction) { friction.push(s); continue; }
    if (s.submitting && !s.completed) { errors.push(s); continue; }   // stuck mid-submit, no category = real issue
    abandonedSessions.push(s);
  }
  const sortDesc = (a) => a.sort((x, y) => y.lastTs - x.lastTs);
  sortDesc(errors); sortDesc(friction); sortDesc(external);
  const reclassFriction = reclassified.filter(s => s._reclass.category === 'friction');
  const reclassExternal = reclassified.filter(s => s._reclass.category === 'external');

  const abandonedByStep = STEPS.map(st => {
    const inStep = abandonedSessions.filter(s => abandonStep(s) === st.key).sort((a, b) => b.lastTs - a.lastTs);
    return { ...st, count: inStep.length, sessions: inStep };
  });
  return {
    reached,
    completed: completedSessions.length,
    stopped: stopped.length,
    conversion: reached ? completedSessions.length / reached : 0,
    critical: errors,                 // `critical` kept as the Errors-bucket alias
    errors, friction, external,
    reclassified, reclassFriction, reclassExternal,
    abandoned: abandonedSessions.length, abandonedByStep,
  };
}

// PII-free error signature for one event: "event|errorName|status" (structured
// fields only — NEVER the message text). Mirrors the SQL array_agg in
// funnelFromNeon so prod and SB-1 produce matchable signatures. Returns '' for
// non-error/warn events.
function errSigOf(e) {
  if (!e || (e.level !== 'error' && e.level !== 'warn')) return '';
  const d = (e.data && typeof e.data === 'object') ? e.data : {};
  const kind = d.errorName || d.name || d.errName || '';
  const st = d.errorStatus != null ? d.errorStatus
    : d.errorCode != null ? d.errorCode
    : d.status != null ? d.status
    : d.statusCode != null ? d.statusCode : '';
  const status = (st === '' || st == null) ? '' : String(st);
  return e.event + '|' + (kind || '') + '|' + status;
}

// Build the same signal rows the SQL produces, from a raw event list (SB-1 path).
function signalsFromEvents(events) {
  const bySid = new Map();
  for (const e of events) {
    const sid = e.sessionId;
    if (!sid) continue;
    let s = bySid.get(sid);
    if (!s) {
      s = { sessionId: sid, maxRank: 0, completed: false, submitting: false,
        clickedPlaceOrder: false, submitError: false, unhandledError: false,
        blurred: false, hidden: false, loggedIn: false,
        hasErrorLevel: false, hasFriction: false, hasExternal: false,
        firstTs: e.timestamp, lastTs: e.timestamp, eventCount: 0, _sigs: new Set() };
      bySid.set(sid, s);
    }
    s.eventCount++;
    const sig = errSigOf(e);
    if (sig) s._sigs.add(sig);
    const cat = e.data && e.data.category;
    if (e.level === 'error') s.hasErrorLevel = true;
    else if (e.level === 'warn' && cat === 'friction') s.hasFriction = true;
    else if (e.level === 'warn' && cat === 'external') s.hasExternal = true;
    const r = RANK_MAP[e.event] || 0;
    if (r > s.maxRank) s.maxRank = r;
    if (e.event === 'order.submit.success') s.completed = true;
    // Resulted in an order even if the success beacon was lost to the post-201
    // redirect: the order-create POST returning 201 is the authoritative signal.
    if (e.event === 'net.xhr.done' && e.data && e.data.method === 'POST'
        && typeof e.data.path === 'string' && e.data.path.includes('/checkout/order')
        && String(e.data.status) === '201') s.completed = true;
    if (e.event === 'customer.signin.success' || e.event === 'customer.create.success' || e.event === 'customer.email-signin.success') s.loggedIn = true;
    if (e.event === 'Payment.handleSubmit.submitOrder.before') s.submitting = true;
    if (e.event === 'Payment.handleSubmit.entered') s.clickedPlaceOrder = true;
    if (e.event === 'Payment.handleSubmit.submitOrder.error') s.submitError = true;
    if (e.event === 'checkout.unhandledError') s.unhandledError = true;
    if (e.event === 'page.blur') s.blurred = true;
    if (e.event === 'page.visibility' && e.data && e.data.state === 'hidden') s.hidden = true;
    if (e.timestamp < s.firstTs) s.firstTs = e.timestamp;
    if (e.timestamp > s.lastTs) s.lastTs = e.timestamp;
  }
  return Array.from(bySid.values())
    .map(s => { const { _sigs, ...rest } = s; return { ...rest, errSigs: Array.from(_sigs) }; })
    .filter(s => s.maxRank >= 1);
}

// Bucket per-session signal rows into per-UTC-day conversion (for the trend view).
// A session is assigned to the UTC day of its first event (session start).
function buildTrend(sessions, startIso, endIso) {
  const byDay = new Map();
  for (const s of sessions) {
    const day = new Date(s.firstTs).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    let b = byDay.get(day);
    if (!b) { b = { day, started: 0, completed: 0 }; byDay.set(day, b); }
    b.started++;
    if (s.completed) b.completed++;
  }
  const out = [];
  for (let d = startIso, g = 0; d <= endIso && g < 400; d = isoAddDays(d, 1), g++) {
    const day = d;
    const b = byDay.get(day) || { day, started: 0, completed: 0 };
    out.push({
      day,
      started: b.started,
      completed: b.completed,
      conversion: b.started ? +(100 * b.completed / b.started).toFixed(1) : 0,
    });
  }
  return out;
}

// ── Time windows (UTC days, matching the Telemetry page convention). ─────────
const dayStartMs = (d) => new Date(d + 'T00:00:00').getTime();
const todayET = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const isoAddDays = (ymd, n) => { const d = new Date(ymd + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toLocaleDateString('en-CA'); };
const PRESETS = [
  { id: 'today',     label: 'Today',        get: () => { const t = todayET(); return [t, t]; } },
  { id: 'yesterday', label: 'Yesterday',    get: () => { const y = isoAddDays(todayET(), -1); return [y, y]; } },
  { id: '7d',        label: 'Last 7 days',  get: () => [isoAddDays(todayET(), -6), todayET()] },
  { id: '30d',       label: 'Last 30 days', get: () => [isoAddDays(todayET(), -29), todayET()] },
];

// ── Small render helpers ─────────────────────────────────────────────────────
const pct = (n) => (n * 100).toFixed(1) + '%';
const ago = (ms) => {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return s + 's ago';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  return Math.floor(s / 86400) + 'd ago';
};

// Absolute local date/time for a session (epoch ms). Title carries full ISO UTC
// for exact Common-BE-log correlation.
const fmtTs = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  const z = (n) => String(n).padStart(2, '0');
  return z(d.getMonth() + 1) + '/' + z(d.getDate()) + ' ' + z(d.getHours()) + ':' + z(d.getMinutes()) + ':' + z(d.getSeconds());
};
function levelOf(eventName) {
  const e = (eventName || '').toLowerCase();
  if (/error|failed|unhandled/.test(e)) return { t: 'ERROR', c: '#f85149' };
  if (/aborted|warn|recovery/.test(e))  return { t: 'WARN',  c: '#d29922' };
  if (/success|completed|\.ok/.test(e)) return { t: 'OK',    c: '#3fb950' };
  return { t: 'INFO', c: '#58a6ff' };
}

const VIEWS = [
  { id: 'trend', label: 'Overview' },
  { id: 'overview', label: 'Details' },
  { id: 'errors', label: 'Errors' },
  { id: 'friction', label: 'Friction' },
  { id: 'external', label: 'External' },
];

export default function CheckoutFunnel() {
  const [env, setEnv] = useState('prod');
  const [startIso, setStartIso] = useState(todayET());
  const [endIso, setEndIso] = useState(todayET());
  const [view, setView] = useState('trend');
  // trend is derived from the in-range sessions (see trendData below)

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);
  const [meta, setMeta] = useState({ scanned: 0, truncated: false, durationMs: 0 });

  const [selSid, setSelSid] = useState(null);
  const [selEvents, setSelEvents] = useState([]);
  const [selLoading, setSelLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [selEmail, setSelEmail] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [errAnalysis, setErrAnalysis] = useState(null);
  const [errLoading, setErrLoading] = useState(false);
  const [errAnalysisError, setErrAnalysisError] = useState(null);
  const [frictionAnalysis, setFrictionAnalysis] = useState(null);
  const [frictionLoading, setFrictionLoading] = useState(false);
  const [frictionError, setFrictionError] = useState(null);
  const [externalAnalysis, setExternalAnalysis] = useState(null);
  const [externalLoading, setExternalLoading] = useState(false);
  const [externalError, setExternalError] = useState(null);
  const [errScope, setErrScope] = useState('critical');   // 'critical' (real issues in view) | 'all' (every error event in window)
  const [rules, setRules] = useState([]);                 // admin reclassification rules (overrides)
  const [rulesBusy, setRulesBusy] = useState(false);

  const reqIdRef = useRef(0);

  const resolveRange = useCallback(() => {
    return { sinceMs: dayStartMs(startIso), untilMs: Math.min(dayStartMs(endIso) + 864e5, Date.now()) };
  }, [startIso, endIso]);

  const fetchAllSandboxEvents = useCallback(async (sinceMs, untilMs) => {
    const PAGE = 1000, MAX_PAGES = 25;
    const seen = new Set();
    const out = [];
    let cursor = untilMs, truncated = false;
    for (let p = 0; p < MAX_PAGES; p++) {
      const qs = new URLSearchParams({ env: 'sandbox', since: String(sinceMs), until: String(cursor), limit: String(PAGE) });
      const res = await fetch(FN + '?' + qs.toString());
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || ('sandbox fetch failed (' + res.status + ')'));
      const batch = Array.isArray(body) ? body : (body.events || []);
      let added = 0, oldest = Infinity;
      for (const e of batch) {
        const id = e._id || (e.timestamp + ':' + e.event + ':' + e.sessionId);
        if (seen.has(id)) continue;
        seen.add(id); out.push(e); added++;
        if (e.timestamp != null && e.timestamp < oldest) oldest = e.timestamp;
      }
      if (batch.length < PAGE || added === 0 || oldest === Infinity || oldest <= sinceMs) break;
      if (p === MAX_PAGES - 1) truncated = true;
      cursor = oldest - 1;
    }
    return { events: out, truncated };
  }, []);

  const load = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    const { sinceMs, untilMs } = resolveRange();
    setLoading(true); setError(null);
    const t0 = Date.now();
    try {
      let rows, scanned = 0, truncated = false;
      if (env === 'prod') {
        const qs = new URLSearchParams({ action: 'funnel', env: 'prod', since: String(sinceMs), until: String(untilMs) });
        const res = await fetch(FN + '?' + qs.toString());
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || ('funnel fetch failed (' + res.status + ')'));
        rows = body.sessions || [];
        scanned = rows.length;
      } else {
        const r = await fetchAllSandboxEvents(sinceMs, untilMs);
        rows = signalsFromEvents(r.events);
        scanned = r.events.length; truncated = r.truncated;
      }
      if (myReq !== reqIdRef.current) return;
      setSessions(rows);
      setMeta({ scanned, truncated, durationMs: Date.now() - t0 });
      setLoadedAt(Date.now());
      setSelSid(null); setSelEvents([]); setExpanded({});
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      setError(e.message || String(e));
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [env, resolveRange, fetchAllSandboxEvents]);

  useEffect(() => { load(); }, [load]);

  // Reclassification rules (admin overrides). Loaded once; CRUD via the
  // checkout-funnel-overrides function. Applied to the funnel at read time.
  const OVR = '/.netlify/functions/checkout-funnel-overrides';
  const loadRules = useCallback(async () => {
    try {
      const res = await fetch(OVR);
      const body = await res.json();
      if (res.ok) setRules(Array.isArray(body.rules) ? body.rules : []);
    } catch (_) { /* non-fatal: funnel still works without overrides */ }
  }, []);
  useEffect(() => { loadRules(); }, [loadRules]);

  const addRule = useCallback(async (rule) => {
    setRulesBusy(true);
    try {
      const res = await fetch(OVR, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rule) });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || ('save failed (' + res.status + ')'));
      // The server keeps ONE session row per sessionId — mirror that client-side
      // so re-clearing/re-reclassifying a session doesn't leave a stale row.
      setRules(prev => {
        const r = body.rule;
        const pruned = (r.scope === 'session')
          ? prev.filter(x => !(x.scope === 'session' && x.sessionId === r.sessionId))
          : prev;
        return [r, ...pruned];
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    } finally {
      setRulesBusy(false);
    }
  }, []);

  const deleteRule = useCallback(async (id) => {
    setRulesBusy(true);
    try {
      const res = await fetch(OVR + '?id=' + encodeURIComponent(id), { method: 'DELETE' });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || ('delete failed (' + res.status + ')')); }
      setRules(prev => prev.filter(r => r.id !== id));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    } finally {
      setRulesBusy(false);
    }
  }, []);

  const f = useMemo(() => classifyFunnel(sessions, rules), [sessions, rules]);
  const inRangeIds = useMemo(() => new Set(sessions.map(s => s.sessionId)), [sessions]);

  // Trend (conversion over time) — reuses action=funnel over a wider window,
  // buckets sessions by start-day client-side. Prod-only (no Neon mirror for SB-1).
  const trendData = useMemo(() => buildTrend(sessions, startIso, endIso), [sessions, startIso, endIso]);

  // From the trend chart/table: open a specific day's snapshot.
  const navigateToDay = useCallback((day) => {
    setStartIso(day);
    setEndIso(day);
    setView('overview');
  }, []);

  const openSession = useCallback(async (sid) => {
    setSelSid(sid); setSelEvents([]); setSelLoading(true); setExpanded({});
    setSelEmail(null); setAiSummary(null); setAiError(null); setAiLoading(false);
    const { sinceMs, untilMs } = resolveRange();
    let evs = [];
    try {
      const qs = new URLSearchParams({ env, sessionId: sid, since: String(sinceMs), until: String(untilMs), limit: '1000' });
      const res = await fetch(FN + '?' + qs.toString());
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || ('session fetch failed (' + res.status + ')'));
      evs = (body.events || []).slice().sort((a, b) => a.timestamp - b.timestamp);
      setSelEvents(evs);
    } catch (e) {
      setSelEvents([{ event: '(load failed)', timestamp: Date.now(), data: { error: e.message } }]);
    } finally {
      setSelLoading(false); // render events immediately; email + AI load after
    }
    if (evs.length === 0) return;

    // Real shopper email from BC by cartId (telemetry strips the local-part). Best-effort.
    let cid = null;
    for (let i = evs.length - 1; i >= 0 && !cid; i--) { const d = evs[i].data; if (d && (d.cartId || d.checkoutId)) cid = d.cartId || d.checkoutId; }
    if (cid) {
      try {
        const er = await fetch('/.netlify/functions/checkout-cart-emails', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cartIds: [cid] }),
        });
        const eb = await er.json();
        if (eb && eb.emails && eb.emails[cid]) setSelEmail(eb.emails[cid]);
      } catch (_) { /* best-effort */ }
    }

    // AI analysis — same endpoint the Telemetry feature uses.
    setAiLoading(true);
    try {
      const ar = await fetch('/.netlify/functions/checkout-telemetry-summary', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: sid, events: evs, mode: 'journey' }),
      });
      const at = await ar.text();
      let parsed; try { parsed = JSON.parse(at); } catch (_) { parsed = { error: 'non-json', raw: at.slice(0, 500) }; }
      if (ar.status !== 200) setAiError({ status: ar.status, body: parsed });
      else setAiSummary(parsed);
    } catch (e) {
      setAiError({ status: 0, body: { error: e.message } });
    } finally {
      setAiLoading(false);
    }
  }, [env, resolveRange]);

  // Stale analysis shouldn't survive a window/env/scope change.
  useEffect(() => {
    setErrAnalysis(null); setErrAnalysisError(null);
    setFrictionAnalysis(null); setFrictionError(null);
    setExternalAnalysis(null); setExternalError(null);
  }, [env, startIso, endIso, errScope]);

  // Error analysis: pull this window's error events, cluster + root-cause them,
  // and surface telemetry gaps (checkout-error-analysis fn). Scope 'critical'
  // restricts to the Real-issues sessions shown above (the "in view" set);
  // 'all' clusters every error-level event in the window.
  const analyzeErrors = useCallback(async () => {
    const { sinceMs, untilMs } = resolveRange();
    setErrLoading(true); setErrAnalysisError(null); setErrAnalysis(null);
    try {
      const qs = new URLSearchParams({ env, level: 'error', since: String(sinceMs), until: String(untilMs), limit: '2000' });
      const er = await fetch(FN + '?' + qs.toString());
      const eb = await er.json();
      if (!er.ok) throw new Error(eb.error || ('error-event fetch failed (' + er.status + ')'));
      let events = Array.isArray(eb) ? eb : (eb.events || []);
      if (errScope === 'critical') {
        const ids = new Set(f.critical.map(s => s.sessionId));
        events = events.filter(e => ids.has(e.sessionId));
      }
      const _rangeLabel = (startIso === endIso ? startIso : startIso + '→' + endIso);
      const label = _rangeLabel
        + (errScope === 'critical' ? ' · real issues' : ' · all errors');
      const ar = await fetch('/.netlify/functions/checkout-error-analysis', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events, windowLabel: label }),
      });
      const at = await ar.text();
      let parsed; try { parsed = JSON.parse(at); } catch (_) { parsed = { error: 'non-json', raw: at.slice(0, 400) }; }
      if (ar.status !== 200) setErrAnalysisError({ status: ar.status, body: parsed });
      else setErrAnalysis(parsed);
    } catch (e) {
      setErrAnalysisError({ status: 0, body: { error: e.message } });
    } finally {
      setErrLoading(false);
    }
  }, [env, resolveRange, startIso, endIso, errScope, f]);

  // Friction/External share the error clusterer. They live at level='warn'
  // tagged with data.category; we fetch the warn stream once and split it
  // client-side (the proxy/webapp don't filter on category).
  const analyzeBucket = useCallback(async (category, setLoadingFn, setAnalysisFn, setErrorFn) => {
    const { sinceMs, untilMs } = resolveRange();
    setLoadingFn(true); setErrorFn(null); setAnalysisFn(null);
    try {
      const qs = new URLSearchParams({ env, level: 'warn', since: String(sinceMs), until: String(untilMs), limit: '2000' });
      const er = await fetch(FN + '?' + qs.toString());
      const eb = await er.json();
      if (!er.ok) throw new Error(eb.error || ('warn-event fetch failed (' + er.status + ')'));
      const allWarn = Array.isArray(eb) ? eb : (eb.events || []);
      const events = allWarn.filter(e => e && e.data && e.data.category === category);
      const _rangeLabel = (startIso === endIso ? startIso : startIso + '→' + endIso);
      const label = _rangeLabel + ' — ' + category;
      const ar = await fetch('/.netlify/functions/checkout-error-analysis', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events, windowLabel: label }),
      });
      const at = await ar.text();
      let parsed; try { parsed = JSON.parse(at); } catch (_) { parsed = { error: 'non-json', raw: at.slice(0, 400) }; }
      if (ar.status !== 200) setErrorFn({ status: ar.status, body: parsed });
      else setAnalysisFn(parsed);
    } catch (e) {
      setErrorFn({ status: 0, body: { error: e.message } });
    } finally {
      setLoadingFn(false);
    }
  }, [env, resolveRange, startIso, endIso]);


  const card = { background: '#fff', border: '1px solid #e6e8eb', borderRadius: 10, padding: 16 };
  const navy = 'var(--navy)';
  const orange = 'var(--orange)';

  return (
    <div style={{ padding: 20, fontFamily: 'Montserrat, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, color: navy, fontSize: 24 }}>Checkout Funnel</h1>
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 2 }}>
            How far checkout sessions get, where they stop, and which stops are real issues.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', border: '1px solid ' + navy, borderRadius: 8, overflow: 'hidden' }}>
            {['prod', 'sandbox'].map(e => (
              <button key={e} onClick={() => setEnv(e)}
                style={{ padding: '6px 12px', border: 'none', cursor: 'pointer', fontSize: 13,
                  background: env === e ? navy : '#fff', color: env === e ? '#fff' : navy }}>
                {e === 'prod' ? 'Production' : 'SB-1'}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', border: '1px solid ' + navy, borderRadius: 8, overflow: 'hidden' }}>
            {PRESETS.map(p => {
              const [ps, pe] = p.get();
              const active = startIso === ps && endIso === pe;
              return (
                <button key={p.id} onClick={() => { setStartIso(ps); setEndIso(pe); }}
                  style={{ padding: '6px 12px', border: 'none', cursor: 'pointer', fontSize: 13,
                    background: active ? orange : '#fff', color: active ? '#fff' : navy }}>
                  {p.label}
                </button>
              );
            })}
          </div>
          <input type="date" value={startIso} max={endIso} onChange={e => setStartIso(e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }} />
          <span style={{ color: '#6b7280', fontSize: 13 }}>to</span>
          <input type="date" value={endIso} min={startIso} max={todayET()} onChange={e => setEndIso(e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }} />
          <button onClick={load} disabled={loading}
            style={{ padding: '6px 14px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13,
              background: navy, color: '#fff', opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
        {loading && <span>Querying {env === 'prod' ? 'Neon mirror' : 'SB-1 webapp'}…</span>}
        {!loading && error && <span style={{ color: '#b91c1c' }}>Error: {error}</span>}
        {!loading && !error && loadedAt && (
          <span>
            {f.reached.toLocaleString()} sessions · {meta.scanned.toLocaleString()} {env === 'prod' ? 'rows' : 'events'} scanned
            · {(meta.durationMs / 1000).toFixed(1)}s · loaded {ago(loadedAt)}
            {meta.truncated && <span style={{ color: '#b45309' }}> · ⚠ window truncated (cap reached) — narrow the range</span>}
          </span>
        )}
      </div>

      {/* View pills (top) */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            style={{ padding: '7px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 13,
              border: '1px solid ' + (view === v.id ? navy : '#d1d5db'),
              background: view === v.id ? navy : '#fff', color: view === v.id ? '#fff' : '#374151',
              fontWeight: view === v.id ? 600 : 500 }}>
            {v.label}
          </button>
        ))}
      </div>

      <div>
        {view === 'trend' && <TrendView data={trendData} funnel={f} onPickDay={navigateToDay} card={card} navy={navy} setView={setView} startIso={startIso} endIso={endIso} />}
        {view === 'overview' && (
          <Overview f={f} card={card} navy={navy} onSelectErrors={() => setView('errors')}
            onSelectFriction={() => setView('friction')} onSelectExternal={() => setView('external')}
            selSid={selSid} selEvents={selEvents} selLoading={selLoading} selEmail={selEmail}
            aiSummary={aiSummary} aiLoading={aiLoading} aiError={aiError}
            expanded={expanded} setExpanded={setExpanded} openSession={openSession} />
        )}
        {view === 'errors' && (
          <BucketView heading="Real issues — Errors" headingColor="#f85149"
            description="Sessions with a genuine technical failure (level=error) or stuck mid-submit to BC with no success. Declines/timeouts now auto-land on Friction/External by their shipped category. Every error stays here until you handle it; click a row for its timeline + AI analysis, or Triage to set status/outcome (or move it)."
            sessions={f.errors} filterCategory="keep" scopeLabel="Real issues"
            selSid={selSid} openSession={openSession} addRule={addRule} rules={rules} inRangeIds={inRangeIds} deleteRule={deleteRule} rulesBusy={rulesBusy} card={card} navy={navy}
            analysis={<ErrorAnalysis criticalCount={f.errors.length} scope={errScope} setScope={setErrScope} analysis={errAnalysis} loading={errLoading} error={errAnalysisError} onRun={analyzeErrors} card={card} navy={navy} />}
            timeline={<SessionTimeline selSid={selSid} selEvents={selEvents} selLoading={selLoading} selEmail={selEmail} aiSummary={aiSummary} aiLoading={aiLoading} aiError={aiError} expanded={expanded} setExpanded={setExpanded} addRule={addRule} rules={rules} rulesBusy={rulesBusy} sessions={sessions} card={card} navy={navy} />} />
        )}
        {view === 'friction' && (
          <BucketView heading="Friction sessions" headingColor="#d29922"
            description="Sessions classified as friction — user-side and recoverable (card/AVS declines, wrong password, dismissed captcha). Telemetry-tagged (level=warn, category=friction) or moved here by you. Triage to set status/outcome or re-place."
            sessions={f.friction} filterCategory="friction" scopeLabel="Friction"
            selSid={selSid} openSession={openSession} addRule={addRule} rules={rules} inRangeIds={inRangeIds} deleteRule={deleteRule} rulesBusy={rulesBusy} card={card} navy={navy}
            analysis={<ErrorAnalysis kind="friction" analysis={frictionAnalysis} loading={frictionLoading} error={frictionError} onRun={() => analyzeBucket('friction', setFrictionLoading, setFrictionAnalysis, setFrictionError)} card={card} navy={navy} />}
            timeline={<SessionTimeline selSid={selSid} selEvents={selEvents} selLoading={selLoading} selEmail={selEmail} aiSummary={aiSummary} aiLoading={aiLoading} aiError={aiError} expanded={expanded} setExpanded={setExpanded} addRule={addRule} rules={rules} rulesBusy={rulesBusy} sessions={sessions} card={card} navy={navy} />} />
        )}
        {view === 'external' && (
          <BucketView heading="External sessions" headingColor="#8957e5"
            description="Sessions classified as external — third-party failures out of our control (BigCommerce / Cloudflare timeouts & errors). Telemetry-tagged (level=warn, category=external) or moved here by you. Triage to set status/outcome or re-place."
            sessions={f.external} filterCategory="external" scopeLabel="External"
            selSid={selSid} openSession={openSession} addRule={addRule} rules={rules} inRangeIds={inRangeIds} deleteRule={deleteRule} rulesBusy={rulesBusy} card={card} navy={navy}
            analysis={<ErrorAnalysis kind="external" analysis={externalAnalysis} loading={externalLoading} error={externalError} onRun={() => analyzeBucket('external', setExternalLoading, setExternalAnalysis, setExternalError)} card={card} navy={navy} />}
            timeline={<SessionTimeline selSid={selSid} selEvents={selEvents} selLoading={selLoading} selEmail={selEmail} aiSummary={aiSummary} aiLoading={aiLoading} aiError={aiError} expanded={expanded} setExpanded={setExpanded} addRule={addRule} rules={rules} rulesBusy={rulesBusy} sessions={sessions} card={card} navy={navy} />} />
        )}
      </div>
    </div>
  );
}

// One tab = three scoped sections + shared timeline. Used identically by Errors,
// Friction and External (the type differs; the structure does not):
//   1. the type's session list (same IssueRow layout, with Triage)
//   2. Triaged — <type> (the review log scoped to this type)
//   3. <type> analysis (passed in)  + 4. session timeline (passed in)
function BucketView({ heading, headingColor, description, sessions, filterCategory, scopeLabel,
  selSid, openSession, addRule, rules, inRangeIds, deleteRule, rulesBusy, card, navy, analysis, timeline }) {
  const [triageTarget, setTriageTarget] = useState(null);
  const [triageErr, setTriageErr] = useState(null);
  const saveTriage = async (rule) => {
    const res = await addRule(rule);
    if (res.ok) { setTriageTarget(null); setTriageErr(null); }
    else setTriageErr(res.error || 'save failed');
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ ...card, borderTop: '3px solid ' + headingColor }}>
        <h3 style={{ marginTop: 0, color: headingColor }}>{heading} ({sessions.length})</h3>
        <p style={{ color: '#6b7280', fontSize: 12.5, marginTop: 0 }}>{description}</p>
        {sessions.length === 0
          ? <div style={{ color: '#6b7280', fontSize: 13 }}>None in this window.</div>
          : <>
              <IssueListHeader showAction />
              <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #eef0f2', borderRadius: 8 }}>
                {sessions.map(s => (
                  <IssueRow key={s.sessionId} s={s} active={selSid === s.sessionId} onClick={() => openSession(s.sessionId)}
                    onReclassify={() => { setTriageTarget(s); setTriageErr(null); }} />
                ))}
              </div>
            </>}
        {triageTarget && (
          <TriageForm session={triageTarget} navy={navy} busy={rulesBusy} error={triageErr}
            onSave={saveTriage} onCancel={() => { setTriageTarget(null); setTriageErr(null); }} />
        )}
      </div>
      <RulesPanel rules={rules} inRangeIds={inRangeIds} deleteRule={deleteRule} busy={rulesBusy} card={card} navy={navy} filterCategory={filterCategory} scopeLabel={scopeLabel} />
      {analysis}
      {timeline}
    </div>
  );
}

// Inline per-session TRIAGE editor: review flag + outcome (tag + note) + optional
// reclassification. One record per session; saving replaces it.
function TriageForm({ session, navy, busy, error, onSave, onCancel }) {
  const sig = topSig(session);
  const cur = session._review || {};
  const [placement, setPlacement] = useState(session._reclass ? session._reclass.category : 'keep');
  const [reviewStatus, setReviewStatus] = useState(cur.status || 'open');
  const [outcomeTag, setOutcomeTag] = useState(cur.tag || '');
  const [note, setNote] = useState(cur.note || '');
  const toggle = (selected, color) => ({
    padding: '5px 11px', border: '1px solid ' + color, borderRadius: 7, cursor: 'pointer', fontSize: 12.5,
    background: selected ? color : '#fff', color: selected ? '#fff' : color,
  });
  const submit = () => onSave({ scope: 'session', sessionId: session.sessionId, category: placement, reviewStatus, outcomeTag: outcomeTag || undefined, note });
  const Field = ({ label, children }) => (
    <div><div style={{ fontSize: 10.5, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div><div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{children}</div></div>
  );
  return (
    <div style={{ marginTop: 12, border: '1px solid #e6e8eb', borderRadius: 10, padding: 12, background: '#fbfcfd' }}>
      <div style={{ fontSize: 12.5, color: '#374151', marginBottom: 10 }}>
        Triage <code style={{ fontFamily: 'monospace' }}>{String(session.sessionId).slice(0, 22)}…</code>
        {sig && <> · <code style={{ fontFamily: 'monospace' }}>{sig.event}{sig.kind ? ' · ' + sig.kind : ''}</code></>}
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Field label="Review status">
          {REVIEW_ORDER.map(id => (
            <button key={id} onClick={() => setReviewStatus(id)} disabled={busy} style={toggle(reviewStatus === id, REVIEW_STATUS[id].color)}>{REVIEW_STATUS[id].label}</button>
          ))}
        </Field>
        <Field label="Place in">
          <button onClick={() => setPlacement('keep')} disabled={busy} style={toggle(placement === 'keep', navy)}>Real issues</button>
          <button onClick={() => setPlacement('friction')} disabled={busy} style={toggle(placement === 'friction', '#d29922')}>Friction</button>
          <button onClick={() => setPlacement('external')} disabled={busy} style={toggle(placement === 'external', '#8957e5')}>External</button>
        </Field>
      </div>
      <div style={{ marginTop: 10 }}>
        <Field label="Outcome tag">
          <button onClick={() => setOutcomeTag('')} disabled={busy} style={toggle(outcomeTag === '', '#9ca3af')}>None</button>
          {Object.keys(OUTCOME_TAG).map(id => (
            <button key={id} onClick={() => setOutcomeTag(id)} disabled={busy} style={toggle(outcomeTag === id, '#374151')}>{OUTCOME_TAG[id]}</button>
          ))}
        </Field>
      </div>
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Outcome / investigation notes — what you found and what was done"
        style={{ width: '100%', boxSizing: 'border-box', marginTop: 10, padding: '7px 9px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12.5, fontFamily: 'inherit', resize: 'vertical' }} />
      {error && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={submit} disabled={busy}
          style={{ padding: '6px 14px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, background: navy, color: '#fff', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} disabled={busy}
          style={{ padding: '6px 14px', border: '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer', fontSize: 13, background: '#fff', color: '#374151' }}>Cancel</button>
      </div>
    </div>
  );
}

// Manage triaged sessions — the captured review log; view + Reset (undo).
function RulesPanel({ rules, deleteRule, busy, card, navy, filterCategory, scopeLabel, inRangeIds }) {
  const [open, setOpen] = useState(false);
  const [delErr, setDelErr] = useState(null);
  const placeColor = (c) => c === 'external' ? '#8957e5' : c === 'friction' ? '#d29922' : '#57606a';
  const placeLabel = (c) => c === 'external' ? 'External' : c === 'friction' ? 'Friction' : 'Real issues';
  const onDelete = async (id) => { const r = await deleteRule(id); if (!r.ok) setDelErr(r.error || 'delete failed'); };
  const sessRules = rules.filter(r => r.scope === 'session' && (!filterCategory || r.category === filterCategory) && (!inRangeIds || inRangeIds.has(r.sessionId)));
  const heading = scopeLabel ? 'Triaged — ' + scopeLabel : 'Triaged sessions';
  return (
    <div style={{ ...card }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
        <h3 style={{ margin: 0, color: navy }}>{heading} ({sessRules.length})</h3>
        <span style={{ color: '#9ca3af', fontSize: 13 }}>{open ? '▾ hide' : '▸ show'}</span>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          <p style={{ color: '#6b7280', fontSize: 12, marginTop: 0 }}>Review log{scopeLabel ? ' for sessions placed in ' + scopeLabel : ''} — status, outcome, and notes. <strong>Reset</strong> returns a session to untriaged.</p>
          {sessRules.length === 0
            ? <div style={{ color: '#6b7280', fontSize: 13 }}>No triaged sessions{scopeLabel ? ' in ' + scopeLabel : ''} yet. Use <strong>Triage</strong> on a row to record one.</div>
            : <div style={{ border: '1px solid #eef0f2', borderRadius: 8 }}>
                {sessRules.map(r => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 12.5 }}>
                    <StatusBadge status={r.reviewStatus} />
                    <TagChip tag={r.outcomeTag} />
                    <span style={{ flexShrink: 0, fontSize: 9.5, fontWeight: 700, color: '#fff', background: placeColor(r.category), borderRadius: 4, padding: '2px 6px' }}>{placeLabel(r.category)}</span>
                    <code style={{ fontFamily: 'monospace', color: '#6b7280', width: 150, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(r.sessionId || '').slice(0, 20)}</code>
                    <span title={r.note} style={{ color: '#374151', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.note || <span style={{ color: '#9ca3af' }}>(no notes)</span>}</span>
                    <button onClick={() => onDelete(r.id)} disabled={busy} title="Undo — return the session to its untriaged state"
                      style={{ flexShrink: 0, padding: '4px 10px', border: '1px solid #f3b1ab', borderRadius: 7, cursor: 'pointer', fontSize: 12, background: '#fff', color: '#b42318' }}>Reset</button>
                  </div>
                ))}
              </div>}
          {delErr && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>{delErr}</div>}
        </div>
      )}
    </div>
  );
}

// ── Presentational subcomponents ─────────────────────────────────────────────
function Tile({ label, value, sub, color, card, onClick }) {
  return (
    <div onClick={onClick} title={onClick ? 'Open' : undefined}
      style={{ ...card, flex: 1, minWidth: 150, ...(onClick ? { cursor: 'pointer' } : {}) }}>
      <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, color: color || 'var(--navy)', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function StageBar({ label, count, total, color }) {
  const w = total ? Math.max(2, (count / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
        <span>{label}</span>
        <span style={{ color: '#6b7280' }}>{count.toLocaleString()} ({total ? ((count / total) * 100).toFixed(1) : '0'}%)</span>
      </div>
      <div style={{ background: '#eef0f2', borderRadius: 6, height: 14 }}>
        <div style={{ width: w + '%', height: '100%', borderRadius: 6, background: color || 'var(--orange)' }} />
      </div>
    </div>
  );
}

function Overview({ f, card, navy, onSelectErrors, onSelectFriction, onSelectExternal, selSid, selEvents, selLoading, selEmail, aiSummary, aiLoading, aiError, expanded, setExpanded, openSession }) {
  const [stageOpen, setStageOpen] = useState({});
  const toggleStage = (rank) => setStageOpen(p => ({ ...p, [rank]: !p[rank] }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* KPI tiles — sessions started, orders, stopped, real issues */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Tile label="Sessions started" value={f.reached.toLocaleString()} sub="reached checkout" card={card} />
        <Tile label="Placed order" value={f.completed.toLocaleString()} sub={pct(f.conversion) + ' conversion'} color="#3fb950" card={card} />
        <Tile label="Abandoned" value={f.abandoned.toLocaleString()} sub="no order, no error — by step below" color="#d29922" card={card} />
        <Tile label="Real issues" value={f.critical.length.toLocaleString()} sub="critical — click to view →" color="#f85149" card={card} onClick={onSelectErrors} />
        <Tile label="Friction" value={f.friction.length.toLocaleString()} sub="user-side — click to view →" color="#d29922" card={card} onClick={onSelectFriction} />
        <Tile label="External" value={f.external.length.toLocaleString()} sub="third-party — click to view →" color="#8957e5" card={card} onClick={onSelectExternal} />
      </div>

      {/* Abandoned by checkout step — non-order, non-critical, bucketed by furthest step */}
      <div style={{ ...card }}>
        <h3 style={{ marginTop: 0, color: navy }}>Abandoned by step ({f.abandoned.toLocaleString()})</h3>
        <p style={{ color: '#6b7280', fontSize: 12.5, marginTop: 0 }}>
          Sessions that didn’t order and had no critical error, bucketed by the furthest checkout step they reached. Click a step to list its sessions; click a session for its timeline.
        </p>
        {f.abandoned === 0 && <div style={{ color: '#6b7280', fontSize: 13 }}>No abandoned sessions in this window.</div>}
        {f.abandonedByStep.filter(st => st.count > 0).map(st => (
          <div key={st.key}>
            <div onClick={() => toggleStage(st.key)} style={{ cursor: 'pointer' }}>
              <StageBar label={(stageOpen[st.key] ? '▼ ' : '▶ ') + st.label} count={st.count} total={f.abandoned}
                color={st.key === 'customer' ? '#9ca3af' : 'var(--orange)'} />
            </div>
            {stageOpen[st.key] && (
              <div style={{ margin: '0 0 12px 12px' }}>
                <IssueListHeader />
                <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #eef0f2', borderRadius: 8 }}>
                  {st.sessions.map(s => (
                    <IssueRow key={s.sessionId} s={s} active={selSid === s.sessionId} onClick={() => openSession(s.sessionId)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Session timeline — loads below when a session is clicked */}
      <SessionTimeline selSid={selSid} selEvents={selEvents} selLoading={selLoading} selEmail={selEmail}
        aiSummary={aiSummary} aiLoading={aiLoading} aiError={aiError}
        expanded={expanded} setExpanded={setExpanded} card={card} navy={navy} />
    </div>
  );
}

// Window-level error analysis — clusters this window's error events across
// sessions, root-causes each, and surfaces telemetry gaps.
function ErrorAnalysis({ criticalCount, scope, setScope, analysis, loading, error, onRun, card, navy, kind = 'error' }) {
  const confColor = (c) => c === 'high' ? '#3fb950' : c === 'low' ? '#f85149' : '#d29922';
  const meta = analysis ? [analysis.modelUsed, (analysis.errorCount != null && analysis.sessionCount != null) ? (analysis.errorCount + ' errors · ' + analysis.sessionCount + ' sessions') : null, (analysis.sampledCount != null ? analysis.sampledCount + ' analyzed' : null), analysis.durationMs ? (analysis.durationMs / 1000).toFixed(1) + 's' : null].filter(Boolean).join(' · ') : '';
  const runLabel = loading ? 'Analyzing…'
    : analysis ? 'Re-run'
    : kind !== 'error' ? ('Analyze ' + kind)
    : scope === 'critical' ? ('Run analysis (' + criticalCount + ' real issue' + (criticalCount === 1 ? '' : 's') + ')')
    : 'Run analysis (all errors in window)';
  const scopes = [{ id: 'critical', label: 'Real issues (' + criticalCount + ')' }, { id: 'all', label: 'All errors' }];
  const title = kind === 'friction' ? 'Friction analysis' : kind === 'external' ? 'External analysis' : 'Error analysis';
  return (
    <div style={{ ...card }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: navy }}>{title}</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {kind === 'error' && (
            <div style={{ display: 'flex', border: '1px solid ' + navy, borderRadius: 8, overflow: 'hidden' }}>
              {scopes.map(s => (
                <button key={s.id} onClick={() => setScope(s.id)} disabled={loading}
                  style={{ padding: '6px 12px', border: 'none', cursor: loading ? 'default' : 'pointer', fontSize: 12.5,
                    background: scope === s.id ? navy : '#fff', color: scope === s.id ? '#fff' : navy }}>
                  {s.label}
                </button>
              ))}
            </div>
          )}
          <button onClick={onRun} disabled={loading}
            style={{ padding: '6px 14px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, background: navy, color: '#fff', opacity: loading ? 0.6 : 1 }}>
            {runLabel}
          </button>
        </div>
      </div>
      <p style={{ color: '#6b7280', fontSize: 12.5, marginTop: 6, marginBottom: analysis || loading || error ? 12 : 0 }}>
        {kind !== 'error'
          ? ('Clusters this window’s ' + kind + ' events by root symptom, proposes a likely cause + confidence for each — and where the data is too thin to be sure, suggests extra telemetry to add.')
          : scope === 'critical'
          ? 'Clusters the error events from the Real-issues sessions above by root symptom, with a likely cause + confidence each — and where the data is thin, suggests extra telemetry to add. Switch to "All errors" to sweep every error-level event in the window (includes login/captcha/vendor noise).'
          : 'Clusters every error-level event in this window. Since the 2026-06-22 classification, friction/external are downgraded to warn — so error-level is mostly genuine defects, plus any pre-2026-06-22 sessions whose declines/timeouts predate the classification.'}
      </p>
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#6b7280', fontSize: 12.5 }}>
          <span className="spin" style={{ width: 12, height: 12, border: '2px solid #cbd5e1', borderTopColor: 'var(--navy)', borderRadius: '50%', display: 'inline-block' }} />
          Claude is clustering the window's errors…
        </div>
      )}
      {error && <div style={{ color: '#b91c1c', fontSize: 12.5 }}>Failed ({error.status}): <code>{JSON.stringify(error.body).slice(0, 200)}</code></div>}
      {analysis && !loading && (
        <div>
          {analysis.windowSummary && <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginBottom: 6 }}>{analysis.windowSummary}</div>}
          {meta && <div style={{ fontSize: 10.5, color: '#9ca3af', marginBottom: 10 }}>{meta}</div>}
          {(analysis.clusters || []).length === 0 && <div style={{ color: '#6b7280', fontSize: 13 }}>No distinct error clusters.</div>}
          {(analysis.clusters || []).map((c, i) => (
            <div key={i} style={{ border: '1px solid #e6e8eb', borderRadius: 10, padding: 12, marginBottom: 10, borderTop: '3px solid ' + (c.severity === 'error' ? '#f85149' : '#d29922') }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                <span style={{ fontWeight: 700, color: navy, fontSize: 13.5 }}>{c.title}</span>
                {c.confidence && <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: '#fff', background: confColor(c.confidence), padding: '2px 6px', borderRadius: 3 }}>{c.confidence} confidence</span>}
                <span style={{ fontSize: 11.5, color: '#9ca3af', marginLeft: 'auto' }}>{(c.sessions != null ? c.sessions + ' sessions' : '')}{c.events != null ? ' · ' + c.events + ' events' : ''}</span>
              </div>
              {c.likelyRootCause && <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5, marginBottom: 6 }}><strong style={{ color: '#9ca3af', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4 }}>Likely cause: </strong>{c.likelyRootCause}</div>}
              {c.evidence && <div style={{ fontSize: 11.5, color: '#6b7280', lineHeight: 1.45, marginBottom: 6, fontFamily: 'monospace' }}>{c.evidence}</div>}
              {Array.isArray(c.telemetryGaps) && c.telemetryGaps.length > 0 && (
                <div style={{ background: '#fffdf5', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px' }}>
                  <strong style={{ color: '#b45309', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>Add telemetry to pin this down</strong>
                  {c.telemetryGaps.map((g, j) => (
                    <div key={j} style={{ fontSize: 12, color: '#374151', lineHeight: 1.5, marginBottom: 6 }}>
                      <div>{g.need}</div>
                      <div style={{ marginTop: 2 }}>
                        {g.addEvent && <code style={{ background: '#eef0f2', padding: '1px 5px', borderRadius: 3 }}>{g.addEvent}</code>}
                        {g.fields && <span style={{ color: '#6b7280' }}> · fields: {g.fields}</span>}
                      </div>
                      {g.where && <div style={{ color: '#9ca3af', fontSize: 11.5, marginTop: 2 }}>where: {g.where}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// AI analysis card — mirrors the Telemetry feature's summary (same endpoint + shape).
function AiSummaryCard({ summary, loading, error, eventCount }) {
  if (loading) return (
    <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, color: '#6b7280', fontSize: 12.5, background: '#f5f8fc', border: '1px solid #e6e8eb', borderRadius: 8, marginBottom: 10 }}>
      <span className="spin" style={{ width: 12, height: 12, border: '2px solid #cbd5e1', borderTopColor: 'var(--navy)', borderRadius: '50%', display: 'inline-block' }} />
      Claude is reviewing the {eventCount} events…
    </div>
  );
  if (error) return (
    <div style={{ padding: '12px 14px', background: '#fde6e6', color: '#7a1818', fontSize: 12, borderRadius: 8, marginBottom: 10 }}>
      AI summary failed ({error.status}): <code>{JSON.stringify(error.body).slice(0, 200)}</code>
    </div>
  );
  if (!summary) return null;
  const sev = summary.severity === 'error' ? '#f85149' : summary.severity === 'warn' ? '#d29922' : '#58a6ff';
  const meta = [summary.modelUsed, (summary.sampledCount != null && summary.eventCount != null) ? (summary.sampledCount + '/' + summary.eventCount + ' events') : null, summary.durationMs ? ((summary.durationMs / 1000).toFixed(1) + 's') : null].filter(Boolean).join(' · ');
  return (
    <div style={{ background: '#f5f8fc', border: '1px solid #e6e8eb', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', background: sev, color: '#fff', padding: '3px 7px', borderRadius: 3 }}>{summary.severity || 'info'}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.4 }}>AI summary</span>
        {summary.knownBugClass && summary.knownBugClass !== 'other' && (
          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#eef0f2', color: '#374151', fontFamily: 'monospace' }}>{summary.knownBugClass}</span>
        )}
        {meta && <span style={{ fontSize: 10.5, color: '#9ca3af', marginLeft: 'auto' }}>{meta}</span>}
      </div>
      {Array.isArray(summary.journey) && summary.journey.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <strong style={{ color: 'var(--navy)', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>What the customer did</strong>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#374151', lineHeight: 1.55 }}>
            {summary.journey.map((step, i) => <li key={i} style={{ marginBottom: 2 }}>{typeof step === 'string' ? step : JSON.stringify(step)}</li>)}
          </ol>
        </div>
      )}
      {summary.summary && <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginBottom: 8 }}>{summary.summary}</div>}
      {summary.rootCause && (
        <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.5, marginBottom: 8 }}>
          <strong style={{ color: '#9ca3af', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4 }}>Root cause: </strong>{summary.rootCause}
        </div>
      )}
      {summary.suggestedFix && (
        <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5, background: '#fff', border: '1px solid #e6e8eb', borderRadius: 4, padding: '8px 10px' }}>
          <strong style={{ color: 'var(--navy)', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>Suggested next step</strong>
          {Array.isArray(summary.suggestedFix)
            ? <ul style={{ margin: 0, paddingLeft: 18 }}>{summary.suggestedFix.map((item, i) => <li key={i} style={{ marginBottom: 3 }}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>)}</ul>
            : <span>{summary.suggestedFix}</span>}
        </div>
      )}
    </div>
  );
}

// "Why was this flagged?" - deterministic (no AI) per-session error analysis.
// Derives one row per distinct level=error signature straight from the session's
// events (reusing errSigOf/parseSig so signatures match the funnel exactly), and
// lets you create the same scope:'signature' rule we'd otherwise POST by hand.
function errorSignatures(events) {
  const map = new Map();
  for (const e of (events || [])) {
    if (!e || e.level !== 'error') continue;
    const sigStr = errSigOf(e);
    if (!sigStr) continue;
    const d = (e.data && typeof e.data === 'object') ? e.data : {};
    let g = map.get(sigStr);
    if (!g) { g = { ...parseSig(sigStr), sigStr, count: 0, msg: '' }; map.set(sigStr, g); }
    g.count++;
    if (!g.msg) g.msg = d.errorMessage || d.message || '';
  }
  return Array.from(map.values());
}

// Recommended bucket for a signature (heuristic; the human still picks).
function suggestCategory(sig) {
  const kind = sig.kind || '', status = String(sig.status || ''), msg = sig.msg || '';
  if (sig.event === 'net.fetch.rejected') return 'external';
  if (/Timeout/i.test(kind) || /CheckoutNotAvailable|Unknown/i.test(kind)) return 'external';
  if (kind === 'Error' && /runtime\.sendMessage|extension|Tab not found/i.test(msg)) return 'external';
  if (status === '0' || status === '429' || /^5\d\d$/.test(status)) return 'external';
  if (/^4\d\d$/.test(status)) return 'friction';
  if (/coupon|already in use|invalid login|password|decline|do(n.?t| not) match/i.test(msg)) return 'friction';
  if (/HostedForm|Validation/i.test(kind)) return 'friction';
  return null;
}

// One-line plain-English reason for a signature.
function reasonFor(sig) {
  const k = sig.kind || '', s = String(sig.status || '');
  if (sig.event === 'net.fetch.rejected') return 'A fetch never completed - network-level failure (connectivity/abort), no server response.';
  if (/Timeout/i.test(k)) return 'A request timed out or aborted (no server response) - network/infra.';
  if (/CheckoutNotAvailable/i.test(k)) return 'The BigCommerce checkout was not found (expired/stale) - third-party state.';
  if (/Unknown/i.test(k)) return 'A content-free rejection with no detail - undiagnosable (often bots/extensions).';
  if (k === 'Error') return 'A generic browser/extension error (e.g. injected content scripts) - not our checkout.';
  if (/MissingData/i.test(k)) return 'The checkout SDK needed data (config/cart/checkout) that was not in its store - usually a load race or bad checkout state. Likely a real bug; investigate before ruling.';
  if (/^4\d\d$/.test(s)) return 'The server rejected the request (client-side ' + s + ') - user-fixable input or state.';
  if (/^5\d\d$/.test(s) || s === '0' || s === '429') return 'A server/infra failure - third-party, outside our control.';
  return 'An error-level event flagged this session - review and classify.';
}

// The analysis block shown atop the Session timeline on the bucket tabs.
function WhyFlagged({ selEvents, selSid, sessions, addRule, rules, rulesBusy }) {
  const sigs = errorSignatures(selEvents);
  if (!sigs.length) return null;
  const signatureRules = (rules || []).filter(r => r.scope === 'signature' && r.event);
  const prevalence = (sigStr) => (sessions || []).filter(s => Array.isArray(s.errSigs) && s.errSigs.includes(sigStr)).length;
  return (
    <div style={{ border: '1px solid #ffd9d9', background: '#fff7f7', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#b42318', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>Why was this flagged?</div>
      <div style={{ fontSize: 11.5, color: '#9ca3af', marginBottom: 8 }}>Error signature(s) that put this session in Real issues - rule away recurring noise, or keep a real one as a bug.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {sigs.map(sig => <SigRuleRow key={sig.sigStr} sig={sig} count={prevalence(sig.sigStr)} signatureRules={signatureRules} addRule={addRule} rulesBusy={rulesBusy} />)}
      </div>
      <KeepAction selSid={selSid} addRule={addRule} rulesBusy={rulesBusy} />
    </div>
  );
}

function KeepAction({ selSid, addRule, rulesBusy }) {
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);
  if (!addRule || !selSid) return null;
  const keep = async () => {
    setErr(null);
    const res = await addRule({ scope: 'session', sessionId: selSid, category: 'keep', reviewStatus: 'open', outcomeTag: 'bug' });
    if (res.ok) setDone(true); else setErr(res.error || 'save failed');
  };
  return (
    <div style={{ borderTop: '1px solid #f3dada', marginTop: 8, paddingTop: 8, fontSize: 12 }}>
      {done
        ? <span style={{ color: '#1a7f37' }}>{'✓'} kept in Real issues, flagged as a bug</span>
        : <>
            <span style={{ color: '#9ca3af' }}>Not noise? </span>
            <button onClick={keep} disabled={rulesBusy} style={{ padding: '4px 12px', border: '1px solid #b42318', borderRadius: 7, cursor: rulesBusy ? 'default' : 'pointer', fontSize: 12.5, background: '#fff', color: '#b42318', opacity: rulesBusy ? 0.6 : 1 }}>
              Keep in Real issues - flag as bug
            </button>
            {err && <span style={{ color: '#b91c1c', marginLeft: 8 }}>{err}</span>}
          </>}
    </div>
  );
}

function SigRuleRow({ sig, count, signatureRules, addRule, rulesBusy }) {
  const [note, setNote] = useState('');
  const [err, setErr] = useState(null);
  const existing = matchSignatureRule({ errSigs: [sig.sigStr] }, signatureRules);
  const rec = suggestCategory(sig);
  const create = async (category) => {
    setErr(null);
    const res = await addRule({ scope: 'signature', event: sig.event, errorName: sig.kind || undefined, status: sig.status || undefined, category, note: note || undefined });
    if (!res.ok) setErr(res.error || 'save failed');
  };
  const label = [sig.event, sig.kind, sig.status].filter(Boolean).join(' · ');
  const btn = (cat, color) => ({ padding: '4px 12px', border: '1px solid ' + color, borderRadius: 7, cursor: rulesBusy ? 'default' : 'pointer', fontSize: 12.5, background: rec === cat ? color : '#fff', color: rec === cat ? '#fff' : color, opacity: rulesBusy ? 0.6 : 1 });
  return (
    <div style={{ borderTop: '1px solid #f3dada', paddingTop: 8, marginTop: 4 }}>
      <div style={{ fontFamily: 'monospace', fontSize: 12.5, color: '#24292f', fontWeight: 600 }}>{label}{sig.count > 1 ? '  x' + sig.count : ''}</div>
      {sig.msg && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{sig.msg.slice(0, 180)}</div>}
      <div style={{ fontSize: 12.5, color: '#374151', marginTop: 4 }}>{reasonFor(sig)}</div>
      <div style={{ fontSize: 11.5, marginTop: 4 }}>
        <span style={{ color: count <= 1 ? '#b45309' : '#57606a', fontWeight: count <= 1 ? 700 : 400 }}>{count <= 1 ? 'one-off (1 session in this window)' : 'seen in ' + count + ' sessions in this window'}</span>
        {rec
          ? <span style={{ color: '#6b7280' }}> · recommended: <strong style={{ color: rec === 'external' ? '#8957e5' : '#d29922' }}>{rec}</strong></span>
          : <span style={{ color: '#b42318' }}> · unrecognized - review (likely a real bug)</span>}
      </div>
      {existing ? (
        <div style={{ fontSize: 12.5, color: '#1a7f37', marginTop: 6 }}>{'✓'} already ruled {'→'} <strong>{existing.category}</strong> (rule #{existing.id})</div>
      ) : addRule ? (
        <div style={{ marginTop: 8 }}>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="optional note (why this rule)" style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12.5, marginBottom: 6 }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11.5, color: '#9ca3af' }}>Rule all like this:</span>
            <button onClick={() => create('friction')} disabled={rulesBusy} style={btn('friction', '#d29922')}>{'→'} Friction</button>
            <button onClick={() => create('external')} disabled={rulesBusy} style={btn('external', '#8957e5')}>{'→'} External</button>
            {err && <span style={{ color: '#b91c1c', fontSize: 12 }}>{err}</span>}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SessionTimeline({ selSid, selEvents, selLoading, selEmail, aiSummary, aiLoading, aiError, expanded, setExpanded, addRule, rules, rulesBusy, sessions, card, navy }) {
  if (!selSid) return null;
  return (
    <div style={{ ...card }}>
      <h4 style={{ marginTop: 0, color: navy }}>
        Session timeline {selLoading && <span style={{ fontSize: 12, color: '#6b7280' }}>· loading…</span>}
      </h4>
      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8, fontFamily: 'monospace' }}>{selSid}{selEvents.length ? ' - ' + fmtTs(selEvents.reduce((m, e) => Math.min(m, e.timestamp), selEvents[0].timestamp)) : ''}</div>
      {selEvents.length > 0 && (() => {
        const sum = sessionSummary(selEvents);
        const Item = ({ label, children }) => <span><span style={{ color: '#9ca3af' }}>{label} </span>{children}</span>;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 13, marginBottom: 10, padding: '10px 12px', background: '#f6f8fa', borderRadius: 8 }}>
            {sum.orderId && <Item label="Order"><strong>#{sum.orderId}</strong></Item>}
            {sum.total != null && <Item label="Total"><strong>${Number(sum.total).toFixed(2)}</strong></Item>}
            {selEmail
              ? <Item label="Email"><strong>{selEmail}</strong></Item>
              : sum.emailDomain && <Item label="Email"><span style={{ color: '#6b7280' }}>(@{sum.emailDomain} — local part not in telemetry)</span></Item>}
            {sum.shipping && <Item label="Ship">{sum.shipping}</Item>}
            {sum.zip && <Item label="Zip">{sum.zip}</Item>}
            {sum.last4 && <Item label="Card">····{sum.last4}</Item>}
            <Item label="Customer">{sum.guest ? 'Guest' : 'Returning'}{sum.vip ? ' · VIP' : ''}</Item>
            {sum.cartId && <span style={{ color: '#c7ccd1', fontFamily: 'monospace' }}>cart {String(sum.cartId).slice(0, 8)}…</span>}
          </div>
        );
      })()}
      {!selLoading && selEvents.length === 0 && <div style={{ color: '#6b7280', fontSize: 13 }}>No events.</div>}
      {addRule && selEvents.length > 0 && <WhyFlagged selEvents={selEvents} selSid={selSid} sessions={sessions} addRule={addRule} rules={rules} rulesBusy={rulesBusy} />}
      {selEvents.length > 0 && <AiSummaryCard summary={aiSummary} loading={aiLoading} error={aiError} eventCount={selEvents.length} />}
      <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
        {selEvents.map((e, i) => {
          const lv = levelOf(e.event);
          const isOpen = !!expanded[i];
          const hasData = e.data && Object.keys(e.data).length > 0;
          return (
            <div key={i} style={{ borderBottom: '1px solid #f1f3f5', padding: '6px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
                <span style={{ width: 52, textAlign: 'center', color: '#fff', background: lv.c, borderRadius: 4, fontSize: 10, padding: '2px 0', fontWeight: 700 }}>{lv.t}</span>
                <span style={{ fontFamily: 'monospace', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.event}</span>
                <span style={{ color: '#9ca3af', fontFamily: 'monospace' }}>{new Date(e.timestamp).toISOString().slice(11, 19)}</span>
                {hasData && (
                  <button onClick={() => setExpanded(p => ({ ...p, [i]: !p[i] }))}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280', fontSize: 12 }}>
                    {isOpen ? '▼' : '▶'}
                  </button>
                )}
              </div>
              {isOpen && hasData && (
                <pre style={{ background: '#0d1117', color: '#c9d1d9', fontSize: 11, padding: 10, borderRadius: 6, overflowX: 'auto', marginTop: 6 }}>
                  {JSON.stringify(e.data, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Column header for the session lists (stage expansions + issue tiers).
function IssueListHeader({ showAction }) {
  const cell = { color: '#9ca3af', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 10px' }}>
      <span style={{ ...cell, flex: 1, minWidth: 0 }}>Session</span>
      {showAction && <span style={{ ...cell, flexShrink: 0 }}>Review</span>}
      <span style={{ ...cell, width: 132, flexShrink: 0 }}>Furthest stage</span>
      <span style={{ ...cell, width: 96, flexShrink: 0 }}>Status</span>
      <span style={{ ...cell, width: 52, textAlign: 'right', flexShrink: 0 }}>Events</span>
      <span style={{ ...cell, width: 110, textAlign: 'right', flexShrink: 0 }}>Started</span>
      {showAction && <span style={{ ...cell, width: 64, textAlign: 'right', flexShrink: 0 }}>Action</span>}
    </div>
  );
}

function IssueRow({ s, active, onClick, onReclassify }) {
  const why = whyStopped(s);
  const rev = s._review;
  return (
    <div onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, cursor: 'pointer',
        background: active ? '#eef4fb' : 'transparent', border: active ? '1px solid #bcd4f0' : '1px solid transparent', fontSize: 12.5 }}>
      <span style={{ fontFamily: 'monospace', color: '#374151', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.sessionId}</span>
      {rev && <span title={rev.note || ''} style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}><StatusBadge status={rev.status} /><TagChip tag={rev.tag} /></span>}
      <span style={{ width: 132, flexShrink: 0, color: '#6b7280' }}>{stageLabel(s.maxRank)}</span>
      <span style={{ width: 96, flexShrink: 0 }}>
        {s.completed
          ? <span style={{ color: '#3fb950', fontWeight: 600 }}>Completed</span>
          : <span style={{ color: WHY[why].color, fontWeight: 600 }}>{WHY[why].label}</span>}
      </span>
      <span style={{ width: 52, flexShrink: 0, textAlign: 'right', color: '#9ca3af' }}>{s.eventCount} ev</span>
      <span title={s.firstTs ? new Date(s.firstTs).toISOString() : ''} style={{ width: 110, flexShrink: 0, textAlign: 'right', color: '#9ca3af' }}>{fmtTs(s.firstTs)}</span>
      {onReclassify && (
        <span style={{ width: 64, flexShrink: 0, textAlign: 'right' }}>
          <button onClick={(e) => { e.stopPropagation(); onReclassify(); }} title="Triage this session — review status, outcome, and where it lands"
            style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 11.5, background: '#fff', color: '#374151' }}>Triage</button>
        </span>
      )}
    </div>
  );
}

// Pull a session-level summary out of its event stream (most-recent value wins).
// Telemetry strips the email local-part (only emailDomain survives); the real
// email is fetched separately via checkout-cart-emails by cartId.
function sessionSummary(events) {
  const pick = (...keys) => {
    for (let i = events.length - 1; i >= 0; i--) {
      const d = events[i].data;
      if (!d) continue;
      for (const k of keys) if (d[k] != null && d[k] !== '') return d[k];
    }
    return null;
  };
  return {
    orderId: pick('orderId'),
    cartId: pick('cartId', 'checkoutId'),
    total: pick('grandTotal', 'cartTotal'),
    shipping: pick('selectedShippingMethod', 'shippingMethod'),
    zip: pick('zipcode'),
    vip: pick('isVIP', 'vipMember') === true,
    guest: pick('customerIsGuest', 'isGuest') === true || pick('current_customer_type') === 'guest',
    emailDomain: pick('emailDomain'),
    last4: pick('last4'),
  };
}

// ── Trend view — conversion over time; click a day to open its snapshot ──────
function TrendView({ data, funnel, onPickDay, card, navy, setView, startIso, endIso }) {
  const tooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, padding: 10, fontSize: 12 }}>
        <div style={{ fontWeight: 700, color: navy, marginBottom: 4 }}>{label}</div>
        <div>Sessions: <strong>{d.started.toLocaleString()}</strong></div>
        <div>Orders: <strong>{d.completed.toLocaleString()}</strong></div>
        <div>Conversion: <strong style={{ color: CHART.orange }}>{d.conversion}%</strong></div>
        <div style={{ color: '#9ca3af', marginTop: 4 }}>click to open this day</div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ ...card }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <h3 style={{ margin: 0, color: navy }}>Conversion over time</h3>
          <div style={{ color: '#6b7280', fontSize: 12.5 }}>{startIso === endIso ? startIso : startIso + ' → ' + endIso}</div>
        </div>
        <p style={{ color: '#6b7280', fontSize: 12.5, marginTop: 6 }}>
          Bars = sessions started; line = conversion %. Click any day to open its snapshot.
        </p>


        {(
          <>
            {funnel && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '8px 0 14px' }}>
                <Tile label="Sessions started" value={funnel.reached.toLocaleString()} sub="reached checkout" card={card} />
                <Tile label="Placed order" value={funnel.completed.toLocaleString()} sub={pct(funnel.conversion) + ' conversion'} color={CHART.green} card={card} />
                <Tile label="Abandoned" value={funnel.abandoned.toLocaleString()} sub="no order, no error" color="#d29922" card={card} />
                <Tile label="Real issues" value={funnel.critical.length.toLocaleString()} sub="critical — need attention" color="#f85149" card={card} />
                <Tile label="Friction" value={funnel.friction.length.toLocaleString()} sub="user-side sessions" color="#d29922" card={card} onClick={() => setView('friction')} />
                <Tile label="External" value={funnel.external.length.toLocaleString()} sub="third-party sessions" color="#8957e5" card={card} onClick={() => setView('external')} />
              </div>
            )}
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                onClick={(e) => { if (e && e.activeLabel) onPickDay(e.activeLabel); }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f2" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" minTickGap={16} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={42} tickFormatter={(v) => v + '%'} domain={[0, (max) => Math.max(10, Math.ceil((max + 5) / 5) * 5)]} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} width={42} allowDecimals={false} />
                <Tooltip content={tooltip} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="right" dataKey="started" name="Sessions" fill={CHART.peach} radius={[2, 2, 0, 0]} cursor="pointer" />
                <Line yAxisId="left" type="monotone" dataKey="conversion" name="Conversion %" stroke={CHART.orange} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </>
        )}
      </div>

      {data.length > 0 && (
        <div style={{ ...card }}>
          <h4 style={{ marginTop: 0, color: navy }}>By day</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: '#6b7280', fontWeight: 700 }}>
                <th style={{ textAlign: 'left', padding: '6px 10px' }}>Day</th>
                <th style={{ textAlign: 'right', padding: '6px 10px' }}>Sessions</th>
                <th style={{ textAlign: 'right', padding: '6px 10px' }}>Orders</th>
                <th style={{ textAlign: 'right', padding: '6px 10px' }}>Conv.</th>
              </tr>
            </thead>
            <tbody>
              {data.slice().reverse().map((d, i) => (
                <tr key={d.day} onClick={() => onPickDay(d.day)}
                  style={{ cursor: 'pointer', background: i % 2 ? '#f6f8fa' : '#fff' }}>
                  <td style={{ padding: '6px 10px', color: navy, fontWeight: 600 }}><span style={{ color: '#9ca3af', marginRight: 6 }}>{new Date(d.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}</span>{d.day}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{d.started.toLocaleString()}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{d.completed.toLocaleString()}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: d.started < 5 ? '#9ca3af' : CHART.orange }}>{d.started ? d.conversion + '%' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
